import { Slack, Mail, Github, Figma, StickyNote } from "lucide-react";

export const SOURCES = {
  slack: { label: "Slack", icon: Slack, color: "text-[#4A154B] dark:text-[#ECB22E]" },
  email: { label: "Email", icon: Mail, color: "text-blue-600 dark:text-blue-400" },
  github: { label: "GitHub", icon: Github, color: "text-stone-800 dark:text-stone-200" },
  figma: { label: "Figma", icon: Figma, color: "text-pink-600 dark:text-pink-400" },
  note: { label: "Note", icon: StickyNote, color: "text-amber-600 dark:text-amber-400" },
};

export const PRIORITIES = {
  high: { label: "High", sym: "+", dot: "bg-red-500", cls: "bg-red-500/10 text-red-600 border-red-500/40 dark:bg-red-950/40 dark:text-red-400" },
  medium: { label: "Medium", sym: "•", dot: "bg-amber-500", cls: "bg-amber-500/10 text-amber-600 border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-400" },
  low: { label: "Low", sym: "–", dot: "bg-emerald-500", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-400" },
};

export const CATEGORY_COLORS = {
  slate: "bg-slate-500/10 text-slate-600 border-slate-500/40 dark:text-slate-300",
  orange: "bg-orange-500/10 text-orange-600 border-orange-500/40 dark:text-orange-400",
  amber: "bg-amber-500/10 text-amber-600 border-amber-500/40 dark:text-amber-400",
  green: "bg-emerald-500/10 text-emerald-600 border-emerald-500/40 dark:text-emerald-400",
  blue: "bg-blue-500/10 text-blue-600 border-blue-500/40 dark:text-blue-400",
  purple: "bg-purple-500/10 text-purple-600 border-purple-500/40 dark:text-purple-400",
  pink: "bg-pink-500/10 text-pink-600 border-pink-500/40 dark:text-pink-400",
  teal: "bg-teal-500/10 text-teal-600 border-teal-500/40 dark:text-teal-400",
};
export const CATEGORY_DOTS = {
  slate: "bg-slate-500", orange: "bg-orange-500", amber: "bg-amber-500", green: "bg-emerald-500",
  blue: "bg-blue-500", purple: "bg-purple-500", pink: "bg-pink-500", teal: "bg-teal-500",
};
export const CATEGORY_COLOR_KEYS = Object.keys(CATEGORY_COLORS);
export const categoryChipCls = (color) => CATEGORY_COLORS[color] || CATEGORY_COLORS.slate;
export const categoryDotCls = (color) => CATEGORY_DOTS[color] || CATEGORY_DOTS.slate;

export const STATUSES = {
  todo: { label: "Open", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  done: { label: "Done", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-400" },
};

function at8am(daysAhead) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(8, 0, 0, 0);
  return d;
}
function nextMonday8am() {
  const d = new Date();
  let diff = (8 - d.getDay()) % 7;
  if (diff === 0) diff = 7;
  d.setDate(d.getDate() + diff);
  d.setHours(8, 0, 0, 0);
  return d;
}

export const SNOOZE_DEFAULTS = [
  { key: "15m", label: "15 minutes", resolve: () => new Date(Date.now() + 15 * 60000) },
  { key: "30m", label: "30 minutes", resolve: () => new Date(Date.now() + 30 * 60000) },
  { key: "1h", label: "1 hour", resolve: () => new Date(Date.now() + 60 * 60000) },
  { key: "2h", label: "2 hours", resolve: () => new Date(Date.now() + 120 * 60000) },
  { key: "4h", label: "4 hours", resolve: () => new Date(Date.now() + 240 * 60000) },
  { key: "tomorrow", label: "Tomorrow", resolve: () => at8am(1) },
  { key: "2days", label: "In 2 days", resolve: () => at8am(2) },
  { key: "nextweek", label: "Next week", resolve: () => nextMonday8am() },
];

const UNIT_MIN = { minutes: 1, hours: 60, days: 1440 };

export function customResolve(c) {
  if (c.kind === "relative") {
    const mins = (Number(c.amount) || 0) * (UNIT_MIN[c.unit] || 1);
    return () => new Date(Date.now() + mins * 60000);
  }
  return () => {
    const d = new Date();
    d.setDate(d.getDate() + (Number(c.daysAhead) || 0));
    d.setHours(Number(c.hour) || 9, Number(c.minute) || 0, 0, 0);
    return d;
  };
}

// Ordered union of all snooze keys (defaults + custom), respecting settings.snooze.order
export function allSnoozeKeys(settings) {
  const custom = settings?.snooze?.custom || [];
  const base = [...SNOOZE_DEFAULTS.map((o) => o.key), ...custom.map((c) => `c_${c.id}`)];
  const order = settings?.snooze?.order || [];
  const ordered = order.filter((k) => base.includes(k));
  const rest = base.filter((k) => !ordered.includes(k));
  return [...ordered, ...rest];
}

// Effective snooze options = enabled defaults + user custom presets, in the user's order
export function buildSnoozeOptions(settings) {
  const disabled = new Set(settings?.snooze?.disabled || []);
  const byKey = {};
  SNOOZE_DEFAULTS.forEach((o) => { byKey[o.key] = o; });
  (settings?.snooze?.custom || []).forEach((c) => {
    byKey[`c_${c.id}`] = { key: `c_${c.id}`, label: c.label, custom: true, resolve: customResolve(c) };
  });
  return allSnoozeKeys(settings).filter((k) => byKey[k] && !disabled.has(k)).map((k) => byKey[k]);
}

export function snoozeLabel(snoozedUntil) {
  if (!snoozedUntil) return "";
  const ms = new Date(snoozedUntil).getTime() - Date.now();
  if (ms <= 0) return "now";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}
